<template>
  <!-- 卡片式轮播：桌面主卡片占容器 50%，移动端放大到 68%（见 cardRatio） -->
  <div class="carousel-container" ref="carouselBox" :style="{ '--card-ratio': cardRatio }">
    <!-- indicator-position 必须显式设为 none：Element 对 type="card" 会强制把指示器
         变成 --outside（position: static），于是这排白底上的浅灰小点会以正常流占掉
         容器下方约 30px，看起来就是轮播图与下一区块之间莫名其妙的空白。 -->
    <el-carousel
        :interval="interval"
        type="card"
        indicator-position="none"
        :height="carouselHeight"
        class="responsive-carousel"
    >
      <el-carousel-item v-for="(item, index) in images" :key="index">
        <div class="card-img-box">
          <img :src="item.url" :alt="item.alt" class="carousel-image">
        </div>
      </el-carousel-item>
    </el-carousel>
  </div>
</template>

<script>
const MOBILE_QUERY = '(max-width: 768px)';
const ASPECT_RATIO = 9 / 16;      // 轮播原图 1920×1080，固定 16:9
const CARD_RATIO_DESKTOP = 0.5;   // 桌面：主卡片占容器宽度的 50%（Element 默认）
const CARD_RATIO_MOBILE = 0.68;   // 移动端：主卡片放大到 68%，否则只有半屏宽、高度会很矮
const MAX_HEIGHT = 620;           // 超宽屏下限制高度，避免轮播图过高

export default {
  name: 'ImageCarousel',
  props: {
    // [{ url, alt }]
    images: {
      type: Array,
      required: true
    },
    interval: {
      type: Number,
      default: 4000
    }
  },
  data() {
    return {
      carouselHeight: '450px',
      isMobile: window.matchMedia(MOBILE_QUERY).matches
    }
  },
  computed: {
    // 主卡片占容器的宽度比例，同时传给 CSS 变量和高度计算，保证两者始终一致
    cardRatio() {
      return this.isMobile ? CARD_RATIO_MOBILE : CARD_RATIO_DESKTOP;
    }
  },
  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.isMobile = window.matchMedia(MOBILE_QUERY).matches;
      this.updateCarouselHeight();
    },
    updateCarouselHeight() {
      this.$nextTick(() => {
        const box = this.$refs.carouselBox;
        if (!box) return;
        const containerWidth = box.clientWidth;
        if (!containerWidth) return;

        // 主卡片宽度 = 容器宽 × 比例
        const cardWidth = containerWidth * this.cardRatio;
        // 直接按原图 16:9 算高，图片既不会被裁切，也不会上下留白
        const calcHeight = Math.round(cardWidth * ASPECT_RATIO);
        this.carouselHeight = `${Math.min(calcHeight, MAX_HEIGHT)}px`;
      });
    }
  }
}
</script>

<style scoped>
/* 轮播图区域 */
.carousel-container {
  width: 95%;
  margin: 20px auto 0 auto;
}

.card-img-box {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

::v-deep .el-carousel__item {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

/* 主卡片宽度改由 --card-ratio 控制（桌面 0.5，移动端 0.68）。
   Element 的位移是按 parentWidth/4 写死的，等于把 50% 宽的主卡片摆在正中；
   宽度变化后会偏离中心，所以用 margin-left 推回来：
   主卡片中心 = marginLeft + parentWidth/4 + 卡片宽/2，令其等于 parentWidth/2，
   解得 marginLeft = (25% - ratio/2) × 容器宽。 */
::v-deep .el-carousel__item--card {
  width: calc(var(--card-ratio, 0.5) * 100%);
  margin-left: calc((0.25 - var(--card-ratio, 0.5) / 2) * 100%);
}

/* 移动端容器铺满屏宽，主卡片才有足够宽度（与 --card-ratio 的断点保持一致） */
@media (max-width: 768px) {
  .carousel-container {
    width: 100%;
  }
}
</style>
